import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Loader2 } from "lucide-react";
import Reveal, { Overline } from "./Reveal";
import { COMPANY, SERVICE_OPTIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-transparent px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-brand";
const labelCls =
  "mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: SERVICE_OPTIONS[0],
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      try {
        await axios.post(`${API}/enquiries`, form);
      } catch (apiErr) {
        try {
          const r = await axios.post("contact.php", form);
          if (!r.data?.ok) throw new Error("php failed");
        } catch (phpErr) {
          const body = encodeURIComponent(
            `Name: ${form.name}\nCompany: ${form.company}\nPhone: ${form.phone}\nService: ${form.service}\n\n${form.message}`
          );
          window.location.href = `mailto:udatmarine@gmail.com?subject=${encodeURIComponent(
            "Website Enquiry - " + form.service
          )}&body=${body}`;
          toast.success("Opening your email app to send the enquiry.");
          return;
        }
      }
      toast.success("Enquiry received — our duty officer will respond shortly.", {
        description: "For urgent matters, call the 24/7 duty line.",
      });
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: SERVICE_OPTIONS[0],
        message: "",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg;
      toast.error(msg || "Could not send your enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="py-24 lg:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
        <div>
          <Reveal>
            <Overline>Contact</Overline>
            <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[0.95] tracking-tighter sm:text-5xl">
              Let's keep your
              <br />
              <span className="font-serif italic font-medium tracking-normal">
                vessel moving
              </span>
              <span className="text-brand">.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 space-y-6">
            <div className="flex gap-4">
              <MapPin size={18} className="mt-0.5 shrink-0 text-brand" />
              <div>
                <p className={labelCls}>Office</p>
                <p className="text-sm leading-relaxed text-slate-200">
                  {COMPANY.address}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Phone size={18} className="mt-0.5 shrink-0 text-brand" />
              <div>
                <p className={labelCls}>Phone · Fax · 24/7 Duty</p>
                <p className="text-sm leading-relaxed text-slate-200">
                  <a
                    data-testid="contact-phone-office"
                    href={`tel:${COMPANY.phoneOffice.replace(/\s/g, "")}`}
                    className="transition-colors hover:text-brand"
                  >
                    {COMPANY.phoneOffice}
                  </a>
                  {" · "}
                  <a
                    data-testid="contact-phone-duty"
                    href={`tel:${COMPANY.phoneDuty.replace(/\s/g, "")}`}
                    className="transition-colors hover:text-brand"
                  >
                    {COMPANY.phoneDuty}
                  </a>
                  <br />
                  <span className="text-steel">Fax {COMPANY.fax}</span>
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Mail size={18} className="mt-0.5 shrink-0 text-brand" />
              <div>
                <p className={labelCls}>Departments</p>
                {COMPANY.emails.map((e) => (
                  <p key={e.email} className="mt-1 text-sm text-slate-200">
                    <span className="text-steel">{e.dept} — </span>
                    <a
                      data-testid={`contact-email-${e.email.split("@")[0]}`}
                      href={`mailto:${e.email}`}
                      className="transition-colors hover:text-brand"
                    >
                      {e.email}
                    </a>
                  </p>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="enquiry-form"
            onSubmit={submit}
            className="border border-line bg-surface/70 p-7 lg:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="f-name">
                  Full Name *
                </label>
                <input
                  id="f-name"
                  data-testid="contact-name-input"
                  className={inputCls}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  required
                  minLength={2}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-company">
                  Company
                </label>
                <input
                  id="f-company"
                  data-testid="contact-company-input"
                  className={inputCls}
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Company / vessel"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-email">
                  Email *
                </label>
                <input
                  id="f-email"
                  data-testid="contact-email-input"
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="f-phone">
                  Phone
                </label>
                <input
                  id="f-phone"
                  data-testid="contact-phone-input"
                  className={inputCls}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+60 ..."
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="f-service">
                  Service Required
                </label>
                <select
                  id="f-service"
                  data-testid="contact-service-select"
                  className={`${inputCls} appearance-none`}
                  value={form.service}
                  onChange={set("service")}
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-surface">
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="f-message">
                  Message *
                </label>
                <textarea
                  id="f-message"
                  data-testid="contact-message-input"
                  className={`${inputCls} min-h-[120px] resize-y`}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Vessel, location, ETA, requirements..."
                  required
                  minLength={5}
                />
              </div>
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-7 flex w-full items-center justify-center gap-2 bg-brand py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
            >
              {sending && <Loader2 size={14} className="animate-spin" />}
              {sending ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
